import { type DriveMimeType, type FlattenedPermission } from '@/shared/DriveTypes';
import { expectType, type ToUnion, type TypeEqual } from '@/shared/types';

export const DEV = import.meta.env?.DEV; // Workers doesn't support for import.meta.env
export const hasWindow = typeof window !== 'undefined';
const allFields = DEV && hasWindow && /(?:\?|&)fields/.test(window.location.search);
export const adminEmail = 'wiki@youneedawiki.com';
const isBun = 'Bun' in globalThis;
export const LEGACY = !isBun && import.meta.env?.VITE_LEGACY === 'true';
export const MODERN = !LEGACY;

export const permissionFields = allFields
  ? '*'
  : 'id,displayName,photoLink,role,type,emailAddress,deleted,permissionDetails(permissionType)';

// Assert that `permissionFields` matches type `Permission`
// This will show a type error when an field without a type is added or a type without a field is added
type PermissionFields = ToUnion<Exclude<typeof permissionFields, '*'>>;
if (import.meta.env?.DEV) expectType<TypeEqual<FlattenedPermission, PermissionFields>>(true);

export const permissionsFields = `permissions(${permissionFields})`;
export const defaultParams = '&supportsAllDrives=true&includeItemsFromAllDrives=true';
export const defaultParamsObj = {
  supportsAllDrives: true,
  includeItemsFromAllDrives: true,
} as const;

// TODO: assert fileFields type against Flattened BaseFile
// https://chatgpt.com/c/682e25d7-66f0-8013-b5f8-4bee83beb99d
export const fileFields = allFields
  ? '*'
  : `id,driveId,name,mimeType,shortcutDetails,trashed,parents,webViewLink,webContentLink,createdTime,modifiedTime,owners(displayName,photoLink,emailAddress),ownedByMe,${permissionsFields},lastModifyingUser(displayName,photoLink,me),properties,capabilities(canEdit,canDelete,canReadDrive),iconLink,size,thumbnailLink,imageMediaMetadata/width,imageMediaMetadata/height,size`;

export const listFields = allFields ? 'files(*)' : `files(${fileFields})`;
export const searchFields = allFields ? 'files(*)' : 'files(id,name,driveId)';

export const mimeTypes = {
  folder: 'application/vnd.google-apps.folder',
  shortcut: 'application/vnd.google-apps.shortcut',
  document: 'application/vnd.google-apps.document',
  drawing: 'application/vnd.google-apps.drawing',
  spreadsheet: 'application/vnd.google-apps.spreadsheet',
  presentation: 'application/vnd.google-apps.presentation',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  form: 'application/vnd.google-apps.form',
  pdf: 'application/pdf',
  mp4: 'video/mp4',
  quicktime: 'video/quicktime',
  markdown: 'text/markdown',
  markdownX: 'text/x-markdown',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  numbers: 'application/x-iwork-numbers-sffnumbers',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  text: 'text/plain',
  url: 'text/x-url',
  html: 'text/html',
  png: 'image/png',
  jpg: 'image/jpeg',
  photoshop: 'image/vnd.adobe.photoshop',
  postscript: 'application/postscript',
  svg: 'image/svg+xml',
  octetStream: 'application/octet-stream',
  json: 'application/json',
  // https://stackoverflow.com/questions/1454777/apple-iwork-mime-types
  pages: 'application/x-iwork-pages-sffpages',
  slideshow: 'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
  csv: 'text/csv',
  zip: 'application/zip',
  word: 'application/msword',
  msShortcut: 'application/x-ms-shortcut',
  igs: 'model/iges',
  ttf: 'application/x-font-ttf',
  otf: 'application/x-font-otf',
  sldworks: 'application/sldworks',
  openSheet: 'application/vnd.oasis.opendocument.spreadsheet',
  oasis: 'application/vnd.oasis.opendocument.text',
  webp: 'image/webp',
  vid: 'application/vnd.google-apps.vid',
  gzip: 'application/x-gzip',
  mpeg: 'video/mpeg',
  xml: 'text/xml',
  illustrator: 'application/illustrator',
  photoshopX: 'image/x-photoshop',
  publisher: 'application/vnd.ms-publisher',
  mp2p: 'video/mp2p',
  excel: 'application/vnd.ms-excel',
  heif: 'image/heif',
  m4a: 'audio/x-m4a',
  mpegAudio: 'audio/mpeg',
  wav: 'audio/x-wav',
  xzip: 'application/x-zip-compressed',
  xmsDownload: 'application/x-msdownload',
  rtf: 'application/rtf',
} as const satisfies Record<string, DriveMimeType>;

export type DriveMimeTypes = typeof mimeTypes;
